import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { normalizeMediaObject, getImageByWidth } from 'api';
import { useAnalytics } from 'hooks';
import { Route, Switch, Redirect } from 'react-router-dom';
import ResizeDetector from 'react-resize-detector';
import { Playlist } from 'msp-components';
import Error404 from 'components/Error404';

const normalizePlaylistMediaItem = (item, imgWidth = 1500, thumbnailWidth = 200) => ({
  ...normalizeMediaObject(item.media, imgWidth, thumbnailWidth),
  id: item.uid,
  type: item.type,
  groupId: item.group,
});

const normalizePlaylistData = (items, imgWidth = 1500, thumbnailWidth = 200) => items.reduce((result, item) => {
  switch (item.type) {
    case 'label':
      result.push({
        id: item.uid,
        type: item.type,
        title: item.name,
      });
      break;
    case 'media':
      result.push(normalizePlaylistMediaItem(item, imgWidth, thumbnailWidth));
      break;
    case 'group': {
      // omit empty groups
      if (!item.items.length > 0) break;
      const thumbnail = (getImageByWidth(get(item, 'groupMedia.previewImages.horizontalThumbnail', []), 200) || {}).url;
      result.push({
        id: item.uid,
        type: item.type,
        title: item.name,
        description: item.description,
        thumbnail,
      });
      item.items.forEach(item => result.push(normalizePlaylistMediaItem(item, imgWidth, thumbnailWidth)));
      break;
    }
    default:
      break;
  }
  return result;
}, []);

const PlaylistTitle = ({ routeProps, items }) => {
  const [containerWidth, setContainerWidth] = useState();
  const handleResize = width => setContainerWidth(width);

  const { match, history } = routeProps;

  const playlistItems = useMemo(() => normalizePlaylistData(items, containerWidth && containerWidth * 1.5), [items, containerWidth]);
  const ids = playlistItems.filter(({ type }) => type === 'media').map(({ id }) => id);

  const [autoplayEnabled, setAutoplayEnabled] = useState(true);
  const [shouldAutoplay, setShouldAutoplay] = useState(false);
  const handleItemSelect = id => {
    setShouldAutoplay(true);
    history.push(`${match.url}/${ids.indexOf(id) + 1}`);
  };
  const handleMediaReady = () => {
    // Reset to stored value
    setShouldAutoplay(autoplayEnabled);
  };

  const { trackMediaPlay, trackMediaProgress } = useAnalytics();

  const handleMediaEnd = itemId => {
    if (autoplayEnabled) {
      const nextIndex = ids.findIndex(id => id === itemId) + 1;
      nextIndex <= ids.length && history.push(`${match.url}/${nextIndex + 1}`);
    }
  };

  return (
    <Switch>
      <Route exact path={match.url} render={props => <Redirect to={`${match.url}/1`} />} />
      <Route
        exact
        path={`${match.url}/:itemIndex`}
        render={props => {
          // Validate index
          // eslint-disable-next-line
          const { itemIndex } = props.match.params;
          const index = parseInt(itemIndex, 10);
          if (isNaN(index) || index < 1 || index > ids.length) return <Error404 />;

          const activeItemId = ids[itemIndex - 1];
          return (
            <>
              <ResizeDetector
                handleWidth
                onResize={handleResize}
                refreshMode="debounce"
                refreshRate={500}
              />
              <Playlist
                items={playlistItems}
                autoplayEnabled={autoplayEnabled}
                shouldAutoplay={shouldAutoplay}
                activeItemId={activeItemId}
                onSelectItem={handleItemSelect}
                onToggleAutoplay={setAutoplayEnabled}
                onMediaReady={handleMediaReady}
                onMediaPlay={trackMediaPlay}
                onMediaProgress={trackMediaProgress}
                onMediaEnd={() => handleMediaEnd(activeItemId)}
              />
            </>
          );
        }}
      />
      <Route component={Error404} />
    </Switch>
  );
};

PlaylistTitle.propTypes = {
  routeProps: PropTypes.object,
  items: PropTypes.arrayOf(PropTypes.object),
};

export default PlaylistTitle;
